// Types for ITSM provider config (ENG-5073) — mirrors the backend
// integrations-core `itsm_provider_config` schema. Config is stored per
// (tenant, provider) on the integrations service and edited wholesale
// via read-modify-write.

export type ItsmProvider =
  | "jira"
  | "jsm"
  | "servicenow"
  | "servicenow-serviceaccount"
  | "freshservice"
  | "asana"
  | "monday"
  | "linear";

// "pm" covers project-management tools (Asana, monday and Linear today,
// ClickUp style tools later).
// They are create-only, so their config is a strict subset of the ITSM one —
// see PmExportRulesSection.
export type ProviderFamily = "jira" | "servicenow" | "freshservice" | "pm";

/**
 * The families with the full ITSM surface. Sections and metadata resolvers
 * that only make sense for a real ITSM take this instead of ProviderFamily,
 * so a PM provider can't be routed into them — several pick their query with
 * a ternary chain whose trailing branch is Freshservice, which would answer
 * for "pm" with the wrong provider's data rather than failing.
 */
export type ItsmProviderFamily = Exclude<ProviderFamily, "pm">;

// Which external providers map to which config family.
export const PROVIDER_FAMILY: Record<string, ProviderFamily> = {
  jira: "jira",
  jsm: "jira",
  servicenow: "servicenow",
  "servicenow-serviceaccount": "servicenow",
  freshservice: "freshservice",
  asana: "pm",
  monday: "pm",
  linear: "pm",
};

/** Matches a ticket whose custom field (by label, desk or catalog form
 *  field) holds any of `values`, case-insensitively. */
export interface CustomFieldMatch {
  name: string;
  values: string[];
}

export interface ExportMatch {
  integration_id?: string | null;
  desk_id?: string | null;
  /** The desk's team (subdesk); requires `desk_id`. */
  subdesk_id?: string | null;
  issue_type?: string | null;
  /** Matches tickets opened from any of these service catalog items. */
  service_catalog_item_ids?: string[] | null;
  custom_field?: CustomFieldMatch | null;
}

export interface GeneralMatch {
  integration_id?: string | null;
  provider_scope?: string | null;
  issue_type?: string | null;
}

export interface InboundConfig {
  watch?: boolean | null;
  since_date?: string | null;
  // ServiceNow and Jira. Integration-wide, not per scope: one rule setting it
  // replaces the whole sweep, so watch and since_date stop applying. Harmony
  // then imports only updates to records it already syncs (ServiceNow: plus
  // their child tasks; Jira: issues it exported or linked) and never creates
  // new tickets. Other providers skip the sync entirely rather than sweep.
  linked_only?: boolean | null;
  // Jira linked-only. Which Harmony fields an inbound update may write; the
  // rest are left alone. Required on every inbound rule while linked-only is
  // on. Values from INBOUND_FIELDS.
  fields?: string[] | null;
  // Jira linked-only. Jira status name (lowercase) → Harmony status. When
  // set it is authoritative: an unmapped Jira status changes nothing.
  status_mapping?: Record<string, string> | null;
  // Jira linked-only. Whether an update may reopen a resolved/closed ticket.
  // Unset = off.
  reopen?: boolean | null;
  // Jira linked-only. Shadow mode: run the full pipeline and log what would
  // change, but write nothing.
  dry_run?: boolean | null;
  // RE2 regex; when set, a watched scope imports only new tickets whose title
  // it matches (searched, so unanchored). Ignored under linked-only.
  title_regex?: string | null;
  // On a tenant with change management (harmony:feature:itil), import this
  // scope's changes as read-only change records on the change calendar. Off,
  // such a tenant imports no changes at all. Ignored without ITIL.
  change_sync?: boolean | null;
}

// Harmony fields a Jira linked-only inbound update can write, as the backend
// accepts in `inbound.fields`. The backend allows more; these are the ones
// the editor offers.
export const INBOUND_FIELDS: { value: string; label: string }[] = [
  { value: "status", label: "Status" },
  { value: "comments", label: "Comments" },
];

export interface Transition {
  comment?: string | null;
  fields?: Record<string, unknown> | null;
}

// "What/where to create" — keyed by Harmony desk, narrowed by catalog item
// and custom field. The most specific matching rule wins; on a tie an
// `exclude` rule wins.
export interface ExportMappingRule {
  match: ExportMatch;
  provider_scope?: string | null;
  issue_type?: string | null;
  excluded_fields?: string[] | null;
  /** Tickets this rule matches are not created in the provider. */
  exclude?: boolean | null;
  close_on_desk_change?: boolean | null;
  create_on_statuses?: string[] | null;
}

/**
 * Export rule for a project-management provider. PM tools nest a task one
 * level deeper than ITSM tools: inside a *lane* within the scope (an Asana
 * section in a project, a Monday group in a board). Mirrors the backend
 * `PmExportMappingRule` — the lane lives on the PM rule, not on every export
 * rule, so ITSM editors can't offer a field their providers have no use for.
 */
export interface PmExportMappingRule extends ExportMappingRule {
  provider_lane?: string | null;
}

// Bidirectional per-scope translation config.
export interface GeneralMappingRule {
  match: GeneralMatch;
  priority_mapping?: Record<string, string> | null;
  status_mapping?: Record<string, string[]> | null;
  custom_field_values?: Record<string, unknown> | null;
  // Whether Harmony pushes ticket updates back out to the provider for this
  // scope. Project-management providers have no updates to push, so for them
  // it is read as the master switch for creating anything at all, carried on
  // their single catch-all rule.
  outbound_sync?: boolean | null;
  inbound?: InboundConfig | null;
}

// One valid external category value (nested sub-categories optional).
// `value` is what outbound sync sends (e.g. the SNOW sys_choice value);
// `label` is the human-facing display text auto-tagging classifies against.
export interface ExternalCategoryOption {
  value: string;
  label: string;
  sub_categories?: ExternalCategoryOption[] | null;
}

/**
 * Which columns on one monday board two-way sync reads and writes. Mirrors
 * the backend `MondayBoardSync`; keyed by board id on `board_sync`, because a
 * column id names a column on one board only.
 */
export interface MondayBoardSync {
  status_column_id?: string | null;
  files_column_id?: string | null;
  assignee_column_id?: string | null;
  /** Harmony custom field definition id → column id. Keyed by id, not name:
   *  fields can be renamed and names repeat across desks. Written only with
   *  `two_way_sync` on: at item creation and on later field changes. */
  custom_field_columns?: Record<string, string> | null;
  /** Keys of `custom_field_columns` written only when the item is created:
   *  later edits in Harmony leave the column alone. */
  create_only_custom_fields?: string[] | null;
  /** Import this board's items as Harmony tickets. Separate from
   *  `two_way_sync`: exporting to a board is not asking for its rows back. */
  import_enabled?: boolean | null;
  /** Desk the board's imported items are created in. Import is live only
   *  with both this and `import_enabled` set. */
  import_desk_id?: string | null;
  /** Harmony fields paired with this board's columns or the item name.
   *  Exclusive with each other and with the slots above; an entry using a
   *  side of a default pairing replaces that default. */
  field_mappings?: MondayFieldMapping[] | null;
}

export type MondayHarmonyFieldKind = "builtin" | "custom" | "catalog";

export type MondaySyncDirection = "both" | "outbound" | "inbound";

/** Mirrors the backend `MondayHarmonyField`: a built-in's name, a custom
 *  field's config field id, or a catalog field's label (with its item). */
export interface MondayHarmonyField {
  kind: MondayHarmonyFieldKind;
  key: string;
  catalog_item_id?: string | null;
}

/** Mirrors the backend `MondayFieldImport` / `JiraFieldImport`: set when
 *  Harmony created the custom field from the provider field it maps. Its
 *  select options then follow the provider's. */
export interface FieldImportSource {
  /** Desk the field was created on; null for a tenant-wide field. */
  desk_id?: string | null;
  /** Jira only: the project whose create screens described the field. */
  project_key?: string | null;
}

export interface MondayFieldMapping {
  harmony: MondayHarmonyField;
  /** Column id, or `"name"` for the item's own name. */
  target: string;
  direction?: MondaySyncDirection;
  imported?: FieldImportSource | null;
}

/** Mirrors the backend `JiraHarmonyField`: the same shape as monday's, with
 *  only the text built-ins (title, description) mappable. */
export type JiraHarmonyField = MondayHarmonyField;

/** Mirrors the backend `JiraFieldMapping`: one Harmony field paired with one
 *  Jira field, tenant-wide. */
export interface JiraFieldMapping {
  harmony: JiraHarmonyField;
  /** Jira field id, e.g. customfield_10050 or labels. */
  target: string;
  direction?: MondaySyncDirection;
  /** Written when the issue is created and never synced after. */
  create_only?: boolean | null;
  imported?: FieldImportSource | null;
}

/** How outbound sync fills one mandatory ServiceNow field.
 *  - predefined: always send `value`, never ask the LLM
 *  - likely: send `value` unless the ticket explicitly says otherwise
 *  - llm: the LLM picks from the field's options */
export type MandatoryFieldStrategy = "predefined" | "likely" | "llm";

export interface MandatoryFieldDefault {
  table: string;
  /** ServiceNow element name, e.g. u_impacted_users. */
  field: string;
  strategy: MandatoryFieldStrategy;
  /** Stored value: choice value, string, or sys_id for a reference field.
   *  Required for predefined and likely; null for llm. */
  value?: string | null;
}

export type AgentChannel = "ticket" | "slack";

// Harmony ticket types, as the backend's agent rules accept.
export const AGENT_TICKET_TYPES = [
  "incident",
  "request",
  "task",
  "change",
  "problem",
] as const;
export type AgentTicketType = (typeof AGENT_TICKET_TYPES)[number];

// What an agent rule matches an imported ticket on. Unset = any.
export interface AgentMatch {
  issue_type?: AgentTicketType | null;
  // Provider record type: ServiceNow table, Jira issue type, FS ticket type.
  external_issue_type?: string | null;
  // Provider catalog item id (ServiceNow cat_item sys_id); the name is only
  // for display.
  catalog_item_id?: string | null;
  catalog_item_name?: string | null;
  // ServiceNow assignment group sys_id / Jira project key.
  provider_scope?: string | null;
}

export interface AgentRule {
  match?: AgentMatch | null;
  // false = the agent leaves matching tickets alone.
  act?: boolean | null;
}

// The AI agent on tickets imported from the integration. No rules = every
// live-imported ticket; otherwise the most specific matching rule decides
// and unmatched tickets are left alone.
export interface AgentConfig {
  enabled?: boolean | null;
  channel?: AgentChannel | null;
  rules?: AgentRule[] | null;
}

// What enabling the agent on an integration with no agent block starts from.
export const DEFAULT_AGENT_CONFIG: AgentConfig = {
  enabled: true,
  channel: "ticket",
  rules: [{ match: { issue_type: "incident" }, act: true }],
};

// The discriminated provider config. All families share export_mapping /
// general_mapping; the rest are provider-specific.
export interface ProviderConfig {
  provider: ItsmProvider;
  // Freshservice: null/omitted allows all groups; [] imports nothing.
  inbound_group_ids?: number[] | null;
  export_mapping?: ExportMappingRule[] | null;
  general_mapping?: GeneralMappingRule[] | null;
  // Every ITSM provider. The AI agent on tickets imported from this
  // integration: whether it works them, which ones, and where it talks to
  // the requester. Unset means off.
  agent?: AgentConfig | null;
  // jira
  transitions?: Record<string, Transition> | null;
  // Tenant-wide. When on, Jira issue links become Harmony ticket links, and a
  // newly imported ticket joins the desk of its earliest-created linked ticket
  // already in Harmony. Tickets already in Harmony are never moved.
  sync_linked_tickets?: boolean | null;
  // Jira field that receives the link back to the Harmony ticket.
  harmony_link_field?: string | null;
  // Harmony fields paired with Jira fields, tenant-wide.
  field_mappings?: JiraFieldMapping[] | null;
  // servicenow
  close_fields?: Record<string, Record<string, string>> | null;
  external_categories?: ExternalCategoryOption[] | null;
  populate_requester_location?: boolean | null;
  // Per integration. ServiceNow is the main ticketing system: the Slack and
  // Teams ticket-created and status messages of a ticket synced to this
  // integration (to the requester and in the desk channel) show its
  // ServiceNow number, and "View Ticket" opens the ServiceNow record.
  main_ticketing_system?: boolean | null;
  // Assignment group (sys_id) for tickets carrying no Harmony team. A
  // ticket that has a team still routes to the team's own group.
  default_assignment_group?: string | null;
  // How each mandatory ServiceNow field is filled on records Harmony creates.
  // Mandatory = dictionary-mandatory or made mandatory by a UI policy that
  // matches the new record; fields with no entry are filled by the LLM.
  mandatory_field_defaults?: MandatoryFieldDefault[] | null;
  // asana — pinned when the rules are configured, because one credential can
  // expose several workspaces and task creation must not guess.
  workspace_gid?: string | null;
  // monday — whether the item keeps syncing both ways once it exists, and
  // the per-board columns that sync uses. The config is replaced whole on
  // save, so both must be carried through every edit.
  two_way_sync?: boolean | null;
  board_sync?: Record<string, MondayBoardSync> | null;
}

export interface IntegrationConfigBody {
  external_categories?: unknown;
  tags?: Record<string, string> | null;
  provider_config?: ProviderConfig | null;
  // Set true once this integration's config is customized via backoffice;
  // locks editing in the customer-facing Harmony UI.
  configuration_customized_by_backoffice?: boolean;
}

export interface IntegrationConfigRead {
  id: string;
  provider: string;
  config: IntegrationConfigBody;
}

// Harmony-side enums — the fixed "key" options for the mappings.
export const HARMONY_PRIORITIES = ["low", "medium", "high", "urgent"] as const;

// The status set accepted by the mapping validators (normalized + granular
// pending_* variants). Mirrors VALID_STATUSES in the backend
// itsm-integration jira/mapping_defaults.py.
export const HARMONY_STATUSES = [
  "open",
  "in_progress",
  "pending",
  "pending_reporter",
  "pending_approval",
  "pending_internal_team",
  "pending_third_party",
  "resolved",
  "closed",
  "canceled",
] as const;

// Human-readable labels for the raw enum keys above. Display-only — the
// stored config values remain the snake_case keys.
export const HARMONY_PRIORITY_LABELS: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

// Harmony statuses an inbound status table may map to. Adds pending_on_hold,
// which the backend accepts for inbound mapping (ENG-9149) but the outbound
// status card does not offer.
export const INBOUND_HARMONY_STATUSES = [
  ...HARMONY_STATUSES,
  "pending_on_hold",
] as const;

export const HARMONY_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In progress",
  pending: "Pending",
  pending_reporter: "Pending – waiting on reporter",
  pending_approval: "Pending – awaiting approval",
  pending_internal_team: "Pending – internal team",
  pending_third_party: "Pending – third party",
  pending_on_hold: "Pending – on hold",
  resolved: "Resolved",
  closed: "Closed",
  canceled: "Canceled",
};

// Harmony ticket fields that outbound-sync can omit on create (excluded_fields).
// Only honored for Jira/JSM today. assignee/reporter/description/priority apply
// on create + update; status/title/comment on update only. value = the literal
// the backend compares against (case-sensitive).
export const EXCLUDABLE_FIELDS: { value: string; label: string }[] = [
  { value: "assignee", label: "Assignee" },
  { value: "reporter", label: "Reporter" },
  { value: "description", label: "Description" },
  { value: "priority", label: "Priority" },
  { value: "status", label: "Status (update only)" },
  { value: "title", label: "Title (update only)" },
  { value: "comment", label: "Comment (update only)" },
];

export const PROVIDER_LABELS: Record<string, string> = {
  jira: "Jira",
  jsm: "Jira Service Management",
  servicenow: "ServiceNow",
  "servicenow-serviceaccount": "ServiceNow (Service Account)",
  freshservice: "Freshservice",
  asana: "Asana",
  monday: "monday.com",
  linear: "Linear",
};
